import React from 'react'
import PropTypes from 'prop-types'
import { FormSection, reduxForm } from 'redux-form'
import { connect } from 'react-redux'
import { compose } from 'redux'
import cls from 'classnames'

import { withFieldChangeEvent } from '@cred/react-analytics/hoc'
import { RadioGroup } from '@cred/react-editors'
import { Testable } from '@cred/react-testable'
import { withStyles } from '@cred/react-theme'

import CheckboxRedux from 'components/checkbox-redux'
import FilterFooter from 'multi-vertical/rate-table/components/filter-modal/footer'
import zipInput from 'components/input/zipInput'
import Input from 'components/input'
import SubmitButton from 'components/submit-button'
import { validateZipWithPrefill, handleChangeZip } from 'common/validations/zip-code'
import DropdownRedux from 'components/dropdown-redux'
import MoneyInputRedux from 'components/money-input-redux/MoneyInputRedux'

import defaultStyles from './themes/default/styles.module.scss'
import multiVerticalStyles from './multiVerticalStyles.module.scss'
import {
  propertyTypes,
  occupancyTypes,
  ficoTypes,
  verticalOptions,
  termsHeaderLabelShortVersion,
} from '../common/options'
import { PURCHASE_TYPE } from '../common/constants'

const RadioGroupWithAnalytics = withFieldChangeEvent(RadioGroup)

const FilterForm = (props) => {
  const {
    isPurchase,
    analyticsContexts = [],
    loadingProducts,
    handleSubmit,
    invalid,
    isMultiVertical,
    cancel,
    onSubmit,
    styles: defaultStylesProps = defaultStyles,
    initialValues: { terms },
    isCashOutError,
    isValidationsError,
  } = props

  const styles = isMultiVertical ? multiVerticalStyles : defaultStylesProps
  const typeAndTermFontType = isMultiVertical ? 'bodySmall' : 'bodyLarge'

  return (
    <Testable className={isMultiVertical ? '' : 'mt-16'} testId="header-body">
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className={styles.formBody}>
          <div className={styles.typeAndTerm}>
            <div className={`${styles.typeContainer} mb-24 mb-lg-0 mr-lg-24`}>
              <p className={cls(styles.title, typeAndTermFontType, 'semiBold')}>Loan type:</p>
              <Testable testId="loanType-buttons">
                <RadioGroupWithAnalytics options={verticalOptions} name="loanType" />
              </Testable>
            </div>
            <div className={styles.termContainer}>
              <p className={cls(styles.title, typeAndTermFontType, 'semiBold')}>Loan term:</p>
              <Testable testId="loan-type-term">
                <FormSection className={styles.checkboxes} name="term">
                  {terms.map((term) => (
                    <CheckboxRedux
                      disableFullWidth
                      name={term}
                      label={termsHeaderLabelShortVersion[term]}
                      className="mr-8"
                      key={term}
                      borderType="noBorder"
                    />
                  ))}
                </FormSection>
              </Testable>
            </div>
          </div>
          <div className={styles.fields}>
            <div className={styles.inputWrapper}>
              <Input
                className="mb-16"
                name={isPurchase ? 'zipCode' : 'zipCodeRefi'}
                label="Zip code"
                placeholder="Zip code"
                validate={validateZipWithPrefill}
                onChange={handleChangeZip}
                component={zipInput}
                analyticsContexts={analyticsContexts}
                numberFormat={(value) => value}
              />
            </div>
            <div className={styles.inputWrapper}>
              <MoneyInputRedux
                className="mb-16"
                name={isPurchase ? 'estimatedValue' : 'homeValue'}
                label={isPurchase ? 'Purchase price' : 'Home value'}
                placeholder={isPurchase ? 'Purchase price' : 'Home value'}
              />
            </div>
            <div className={styles.inputWrapper}>
              <MoneyInputRedux
                className="mb-16"
                name={isPurchase ? 'downPayment' : 'loanAmount'}
                label={isPurchase ? 'Estimated down payment' : 'Loan amount'}
                placeholder={isPurchase ? 'Estimated down payment' : 'Loan amount'}
              />
            </div>
            <div className={styles.inputWrapper}>
              <DropdownRedux
                className="mb-16"
                label="Property type"
                name={isPurchase ? 'propertyType' : 'propertyTypeRefi'}
                options={propertyTypes}
                icon={false}
                analyticsOptions={{ fieldLabel: 'Property type' }}
              />
            </div>
            <div className={styles.inputWrapper}>
              <DropdownRedux
                label="Property usage"
                className="mb-16"
                name={isPurchase ? 'occupancyType' : 'occupancyTypeRefi'}
                options={occupancyTypes}
                icon={false}
                analyticsOptions={{ fieldLabel: 'Property usage' }}
              />
            </div>
            <div className={styles.inputWrapper}>
              <DropdownRedux
                className="mb-16"
                label="Credit score range"
                name={isPurchase ? 'ficoType' : 'ficoTypeRefi'}
                options={ficoTypes}
                icon={false}
                analyticsOptions={{ fieldLabel: 'Credit score range' }}
              />
            </div>
            {!isPurchase && !isMultiVertical && (
              <div className={styles.inputWrapper}>
                <MoneyInputRedux
                  name="cashOut"
                  label="Cash out amount"
                  placeholder="Cash out amount"
                />
              </div>
            )}
            <div className={styles.inputWrapper}>
              {!isMultiVertical && (
                <SubmitButton
                  data-test-id="submitBTN"
                  className={styles.button}
                  analyticsContexts={analyticsContexts}
                  loading={loadingProducts}
                  disabled={invalid || isCashOutError || isValidationsError}
                >
                  Update
                </SubmitButton>
              )}
            </div>
          </div>
        </section>
        {isMultiVertical && <FilterFooter cancel={cancel} update={onSubmit} invalid={invalid} />}
      </form>
    </Testable>
  )
}

FilterForm.propTypes = {
  /* eslint-disable-next-line */
  isPurchase: PropTypes.bool.isRequired,
  invalid: PropTypes.bool,
  analyticsContexts: PropTypes.array,
  loadingProducts: PropTypes.bool.isRequired,
  isCashOutError: PropTypes.bool.isRequired,
  isValidationsError: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isMultiVertical: PropTypes.bool,
  styles: PropTypes.object,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  // Cancel are only
  // used for the multi-vertical widget
  cancel: PropTypes.func,
}

const mapStateToProps = ({ form }, { form: formName }) => {
  const formValues = form[formName] && form[formName].values
  return {
    isPurchase: typeof formValues === 'object' && formValues.loanType === PURCHASE_TYPE,
  }
}

export { FilterForm }

export default compose(
  withStyles(defaultStyles),
  reduxForm({
    keepDirtyOnReinitialize: false,
    enableReinitialize: true,
    destroyOnUnmount: false,
  }),
  connect(mapStateToProps),
)(FilterForm)
